"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";
import { CheckCircle2, AlertCircle } from "lucide-react";
import {
  applySchema,
  CHECK_SIZES,
  OFFERING_CHOICES,
  type ApplyPayload,
  type OfferingChoice,
} from "@/lib/apply-schema";
import {
  INVESTOR_TYPES,
  ACCREDITATION_METHODS,
  US_STATES,
} from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type SubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "success"; offering: OfferingChoice }
  | { status: "error"; message: string };

export default function ApplyPage() {
  const [submit, setSubmit] = useState<SubmitState>({ status: "idle" });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ApplyPayload>({
    resolver: zodResolver(applySchema),
    defaultValues: {
      consentVerification: false as unknown as true,
      consentRegD: false as unknown as true,
    },
  });

  const onSubmit = async (data: ApplyPayload) => {
    setSubmit({ status: "submitting" });
    try {
      const res = await fetch("/api/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Request failed (${res.status})`);
      }
      setSubmit({ status: "success", offering: data.offering });
    } catch (e) {
      setSubmit({
        status: "error",
        message: e instanceof Error ? e.message : "Something went wrong",
      });
    }
  };

  if (submit.status === "success") {
    const offeringLine =
      submit.offering === "Both"
        ? "the LexGoAI and AIDirect waitlists"
        : `the ${submit.offering} waitlist`;
    return (
      <article className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 pt-32 pb-24">
        <div className="rounded-xl border border-teal/40 bg-teal/5 p-8 text-center space-y-4">
          <CheckCircle2 className="size-12 text-teal mx-auto" />
          <h1 className="font-heading text-3xl font-bold text-navy">
            You&apos;re on {offeringLine}
          </h1>
          <p className="text-slate">
            Thanks. No subscription has been processed and no securities have
            been sold. We&apos;ll email you when offering documents are
            finalized and applications formally open. At that point, you can
            complete your application and our third-party
            accreditation-verification provider will reach out to verify
            accredited status.
          </p>
          <p className="text-sm text-slate">
            Questions in the meantime:{" "}
            <a
              className="text-teal hover:underline"
              href="mailto:invest@lexgoinvest.ai"
            >
              invest@lexgoinvest.ai
            </a>
            .
          </p>
        </div>
      </article>
    );
  }

  return (
    <article className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 pt-32 pb-24">
      {/*
        Waitlist banner — required while offering documents are pre-counsel.
        To turn off waitlist mode, see deploy/PRODUCTION-CUTOVER.md.
        (Don't just delete this block — there's a queued git branch
        flip-back-from-waitlist that reverts /apply cleanly.)
      */}
      <div className="mb-10 rounded-xl border border-gold/40 bg-gold/10 p-5">
        <p className="font-heading font-semibold text-navy">
          Applications are open on a waitlist basis only.
        </p>
        <p className="text-sm text-slate mt-2">
          Offering documents are pending finalization with securities counsel.
          <strong className="text-navy">
            {" "}
            No subscription will be processed and no securities will be sold
            until offering documents are finalized.
          </strong>{" "}
          Submitting this form adds you to a waitlist; the third-party
          accreditation-verification provider is <em>not</em> engaged on
          waitlist submissions. When applications formally open we&apos;ll
          email you and you can complete your submission then.
        </p>
      </div>

      <header className="space-y-3 mb-10">
        <p className="text-sm font-semibold uppercase tracking-widest text-teal">
          Reg D 506(c) &middot; Waitlist
        </p>
        <h1 className="font-heading text-4xl font-bold text-navy">
          Join the waitlist
        </h1>
        <p className="text-slate">
          ~5 minutes. We use the information below only to contact you when
          applications open and to gauge interest while offering documents are
          finalized. The issuer does not handle tax returns or account
          statements at any stage; that&apos;s the third-party provider, and
          they aren&apos;t engaged on waitlist submissions.
        </p>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Offering picker — first question. Each option carries the cap-
            table economics so the applicant can see what they're electing
            into before they fill in personal info. "Both" creates two
            separate SAFE subscriptions on the backend, not one bundled
            instrument. */}
        <Field
          label="Which offering are you applying to?"
          hint="LexGoAI and AIDirect are distinct issuers. You can subscribe to one, the other, or both — but each is a separate SAFE you sign individually."
          error={errors.offering?.message}
        >
          <fieldset
            className="grid gap-3 sm:grid-cols-3"
            aria-describedby="offering-help"
          >
            <legend className="sr-only">Offering</legend>
            <OfferingCard
              value="LexGoAI"
              register={register("offering")}
              title="LexGoAI, Inc."
              eyebrow="Technology"
              terms={[
                ["Round size", "$2,000,000"],
                ["Post-money cap", "$10,000,000"],
                ["Min check", "$50,000"],
              ]}
              accent="border-teal/40 has-[:checked]:border-teal has-[:checked]:bg-teal/5"
            />
            <OfferingCard
              value="AIDirect"
              register={register("offering")}
              title="AIDirect, Inc."
              eyebrow="AI-first legal services"
              terms={[
                ["Round size", "$500,000"],
                ["Post-money cap", "$3,000,000"],
                ["Min check", "$50,000"],
              ]}
              accent="border-gold/40 has-[:checked]:border-gold-dark has-[:checked]:bg-gold/10"
            />
            <OfferingCard
              value="Both"
              register={register("offering")}
              title="Both"
              eyebrow="Two separate SAFEs"
              terms={[
                ["LexGoAI", "$10M post-money"],
                ["AIDirect", "$3M post-money"],
                ["Min check", "$50,000 each"],
              ]}
              accent="border-navy/30 has-[:checked]:border-navy has-[:checked]:bg-navy/5"
            />
          </fieldset>
        </Field>

        <Field label="Legal name" error={errors.legalName?.message}>
          <Input
            {...register("legalName")}
            placeholder="As it appears on ID"
            autoComplete="name"
            autoCapitalize="words"
            spellCheck={false}
          />
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Email" error={errors.email?.message}>
            <Input
              type="email"
              {...register("email")}
              placeholder="you@example.com"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
            />
          </Field>
          <Field label="Phone" error={errors.phone?.message}>
            <Input
              type="tel"
              {...register("phone")}
              placeholder="+1 555 555 5555"
              autoComplete="tel"
              inputMode="tel"
            />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="State of residence" error={errors.state?.message}>
            <NativeSelect
              {...register("state")}
              autoComplete="address-level1"
            >
              <option value="">Select…</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Investor type" error={errors.investorType?.message}>
            <NativeSelect {...register("investorType")}>
              <option value="">Select…</option>
              {INVESTOR_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </NativeSelect>
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Expected check size" error={errors.checkSize?.message}>
            <NativeSelect {...register("checkSize")}>
              <option value="">Select…</option>
              {CHECK_SIZES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field
            label="Investment vehicle"
            hint="self / SDIRA / LLC / trust / fund"
            error={errors.vehicle?.message}
          >
            <Input {...register("vehicle")} placeholder="e.g. Self-directed IRA" />
          </Field>
        </div>

        <Field
          label="Source of funds"
          hint="Brief description (savings, business sale, fund commitment, etc.)"
          error={errors.sourceOfFunds?.message}
        >
          <Textarea rows={4} {...register("sourceOfFunds")} />
        </Field>

        <Field
          label="Accreditation method"
          hint="The SEC safe-harbor category you expect to verify under."
          error={errors.accreditationMethod?.message}
        >
          <NativeSelect {...register("accreditationMethod")}>
            <option value="">Select…</option>
            {ACCREDITATION_METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </NativeSelect>
        </Field>

        <div className="space-y-3 rounded-lg border border-border bg-muted p-4 text-sm">
          <ConsentRow
            label={
              <>
                I understand this is a waitlist signup, not a subscription or
                an offer. I consent to LexGo Invest contacting me when
                applications formally open and, at that time, to engaging a
                third-party verification provider to verify my accredited
                status under Rule 506(c).
              </>
            }
            register={register("consentVerification")}
            error={errors.consentVerification?.message}
          />
          <ConsentRow
            label={
              <>
                I have read the{" "}
                <Link
                  href="/legal/disclosures"
                  className="text-teal underline"
                  target="_blank"
                >
                  Disclosures
                </Link>{" "}
                (which are themselves pending counsel finalization) and
                understand that, when the offering opens, it will be conducted
                under Reg D 506(c) to accredited investors only, and that any
                investment will be illiquid and high-risk.
              </>
            }
            register={register("consentRegD")}
            error={errors.consentRegD?.message}
          />
        </div>

        {submit.status === "error" && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{submit.message}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={submit.status === "submitting"}
          className="w-full rounded-lg bg-navy px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-navy/90 disabled:opacity-50"
        >
          {submit.status === "submitting" ? "Adding to waitlist…" : "Join the waitlist"}
        </button>

        <p className="text-xs text-slate">
          Joining the waitlist does not constitute an offer, a subscription, or
          a securities transaction. No verification provider is engaged on
          waitlist submissions. We&apos;ll email you when applications open and
          you can complete your submission then.
        </p>
      </form>
    </article>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {hint && <p className="text-xs text-slate">{hint}</p>}
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function NativeSelect(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm transition-colors outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20"
    />
  );
}

function ConsentRow({
  label,
  register,
  error,
}: {
  label: React.ReactNode;
  register: ReturnType<ReturnType<typeof useForm<ApplyPayload>>["register"]>;
  error?: string;
}) {
  return (
    <label className="flex items-start gap-3 text-sm text-slate cursor-pointer">
      <input
        type="checkbox"
        {...register}
        className="size-4 mt-0.5 rounded-[4px] border-input"
      />
      <span>
        {label}
        {error && (
          <span className="block text-xs text-destructive mt-1">{error}</span>
        )}
      </span>
    </label>
  );
}

function OfferingCard({
  value,
  register,
  title,
  eyebrow,
  terms,
  accent,
}: {
  value: OfferingChoice;
  register: ReturnType<ReturnType<typeof useForm<ApplyPayload>>["register"]>;
  title: string;
  eyebrow: string;
  terms: ReadonlyArray<readonly [string, string]>;
  accent: string;
}) {
  return (
    <label
      className={`relative flex flex-col gap-2 rounded-xl border p-4 cursor-pointer transition-colors hover:bg-muted/40 ${accent}`}
    >
      <input
        type="radio"
        value={value}
        {...register}
        className="sr-only peer"
      />
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate">
        {eyebrow}
      </p>
      <p className="font-heading font-bold text-navy">{title}</p>
      <dl className="space-y-0.5 text-xs text-slate">
        {terms.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2">
            <dt>{k}</dt>
            <dd className="font-medium text-navy">{v}</dd>
          </div>
        ))}
      </dl>
      {/* Decorative selected-state ring; the actual a11y signal is
          peer-checked via the sr-only radio above. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-transparent peer-checked:ring-current"
      />
    </label>
  );
}
